import { useState, type FormEvent } from 'react'
import {
  CAlert,
  CButton,
  CButtonGroup,
  CForm,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from '@coreui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { consoleApi, apiErrorMessage } from '../../api/client'
import type { IncidentRecord, MaintenanceRecord } from '../../api/types'
import { EmptyState, ErrorState, LoadingState } from '../../components/common/AsyncState'
import { PageHeader } from '../../components/common/PageHeader'
import { formatDateTime } from '../../utils/format'

const local = (date: Date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
const incidentInitial = () => ({
  title: '',
  severity: 'medium',
  status: 'detected',
  detectedAt: local(new Date()),
  resolvedAt: '',
  publicSummary: '',
  internalNotes: '',
})
const maintenanceInitial = () => ({
  title: '',
  scope: '',
  status: 'planned',
  startsAt: local(new Date(Date.now() + 24 * 3_600_000)),
  endsAt: '',
  result: '',
  nextDueAt: '',
  publicSummary: '',
  internalNotes: '',
})

export function OperationsPage() {
  const client = useQueryClient()
  const [tab, setTab] = useState<'incidents' | 'maintenance'>('incidents')
  const [modal, setModal] = useState(false)
  const [incidentForm, setIncidentForm] = useState(incidentInitial)
  const [maintenanceForm, setMaintenanceForm] = useState(maintenanceInitial)
  const incidents = useQuery({ queryKey: ['console', 'incidents'], queryFn: consoleApi.incidents })
  const maintenance = useQuery({ queryKey: ['console', 'maintenance'], queryFn: consoleApi.maintenance })
  const createIncident = useMutation({
    mutationFn: consoleApi.createIncident,
    onSuccess: async () => {
      setModal(false)
      setIncidentForm(incidentInitial())
      await client.invalidateQueries({ queryKey: ['console', 'incidents'] })
    },
  })
  const updateIncident = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      consoleApi.updateIncident(id, input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['console', 'incidents'] }),
  })
  const createMaintenance = useMutation({
    mutationFn: consoleApi.createMaintenance,
    onSuccess: async () => {
      setModal(false)
      setMaintenanceForm(maintenanceInitial())
      await client.invalidateQueries({ queryKey: ['console', 'maintenance'] })
    },
  })
  const updateMaintenance = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Record<string, unknown> }) =>
      consoleApi.updateMaintenance(id, input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['console', 'maintenance'] }),
  })
  const error =
    createIncident.error ?? updateIncident.error ?? createMaintenance.error ?? updateMaintenance.error
  const submitIncident = (event: FormEvent) => {
    event.preventDefault()
    createIncident.mutate({
      ...incidentForm,
      detectedAt: new Date(incidentForm.detectedAt).toISOString(),
      resolvedAt: incidentForm.resolvedAt ? new Date(incidentForm.resolvedAt).toISOString() : null,
      ...(incidentForm.publicSummary ? { publicSummary: incidentForm.publicSummary } : {}),
      ...(incidentForm.internalNotes ? { internalNotes: incidentForm.internalNotes } : {}),
    })
  }
  const submitMaintenance = (event: FormEvent) => {
    event.preventDefault()
    createMaintenance.mutate({
      ...maintenanceForm,
      startsAt: new Date(maintenanceForm.startsAt).toISOString(),
      endsAt: maintenanceForm.endsAt ? new Date(maintenanceForm.endsAt).toISOString() : null,
      nextDueAt: maintenanceForm.nextDueAt ? new Date(maintenanceForm.nextDueAt).toISOString() : null,
      ...(maintenanceForm.result ? { result: maintenanceForm.result } : {}),
      ...(maintenanceForm.publicSummary ? { publicSummary: maintenanceForm.publicSummary } : {}),
      ...(maintenanceForm.internalNotes ? { internalNotes: maintenanceForm.internalNotes } : {}),
    })
  }
  const incidentStatus = (item: IncidentRecord, status: IncidentRecord['status']) =>
    updateIncident.mutate({
      id: item._id,
      input: {
        title: item.title,
        severity: item.severity,
        status,
        detectedAt: item.detectedAt,
        resolvedAt: ['resolved', 'reviewed'].includes(status)
          ? (item.resolvedAt ?? new Date().toISOString())
          : (item.resolvedAt ?? null),
        ...(item.publicSummary ? { publicSummary: item.publicSummary } : {}),
        ...(item.internalNotes ? { internalNotes: item.internalNotes } : {}),
      },
    })
  const maintenanceStatus = (item: MaintenanceRecord, status: MaintenanceRecord['status']) =>
    updateMaintenance.mutate({
      id: item._id,
      input: {
        title: item.title,
        scope: item.scope,
        status,
        startsAt: item.startsAt,
        endsAt:
          status === 'completed' ? (item.endsAt ?? new Date().toISOString()) : (item.endsAt ?? null),
        nextDueAt: item.nextDueAt ?? null,
        ...(item.result ? { result: item.result } : {}),
        ...(item.publicSummary ? { publicSummary: item.publicSummary } : {}),
        ...(item.internalNotes ? { internalNotes: item.internalNotes } : {}),
      },
    })
  return (
    <>
      <PageHeader
        eyebrow="Machine preservation"
        title="Incidents & maintenance"
        description="Record the full internal lifecycle, then create a separate public-safe audit draft when an event should enter the Compute Ledger."
        actions={
          <CButton color="primary" onClick={() => setModal(true)}>
            {tab === 'incidents' ? 'Open incident' : 'Plan maintenance'}
          </CButton>
        }
      />
      <div className="filter-row filter-row--console">
        <CButtonGroup>
          <CButton color={tab === 'incidents' ? 'primary' : 'light'} onClick={() => setTab('incidents')}>
            Incidents
          </CButton>
          <CButton
            color={tab === 'maintenance' ? 'primary' : 'light'}
            onClick={() => setTab('maintenance')}
          >
            Maintenance
          </CButton>
        </CButtonGroup>
      </div>
      {error && <CAlert color="danger">{apiErrorMessage(error)}</CAlert>}
      {tab === 'incidents' && (
        <>
          {incidents.isLoading && <LoadingState />}
          {incidents.isError && <ErrorState message={apiErrorMessage(incidents.error)} />}
          {incidents.data &&
            (incidents.data.length ? (
              <div className="operations-list">
                {incidents.data.map((item) => (
                  <article key={item._id}>
                    <div className="operations-list__heading">
                      <div>
                        <span className={`severity severity-${item.severity}`}>{item.severity}</span>
                        <h2>{item.title}</h2>
                      </div>
                      <CFormSelect
                        value={item.status}
                        onChange={(event) =>
                          incidentStatus(item, event.target.value as IncidentRecord['status'])
                        }
                      >
                        <option value="detected">Detected</option>
                        <option value="acknowledged">Acknowledged</option>
                        <option value="investigating">Investigating</option>
                        <option value="mitigated">Mitigated</option>
                        <option value="resolved">Resolved</option>
                        <option value="reviewed">Reviewed</option>
                      </CFormSelect>
                    </div>
                    <p>{item.publicSummary ?? 'No public summary has been prepared.'}</p>
                    <div className="record-meta">
                      <span>Detected {formatDateTime(item.detectedAt)}</span>
                      <span>Created by {item.createdBy}</span>
                    </div>
                    {item.internalNotes && (
                      <details>
                        <summary>Internal notes</summary>
                        <p>{item.internalNotes}</p>
                      </details>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No incident records"
                description="This is good news. Use Open incident when an operational issue is detected."
              />
            ))}
        </>
      )}
      {tab === 'maintenance' && (
        <>
          {maintenance.isLoading && <LoadingState />}
          {maintenance.isError && <ErrorState message={apiErrorMessage(maintenance.error)} />}
          {maintenance.data &&
            (maintenance.data.length ? (
              <div className="operations-list">
                {maintenance.data.map((item) => (
                  <article key={item._id}>
                    <div className="operations-list__heading">
                      <div>
                        <span className="category">maintenance</span>
                        <h2>{item.title}</h2>
                      </div>
                      <CFormSelect
                        value={item.status}
                        onChange={(event) =>
                          maintenanceStatus(item, event.target.value as MaintenanceRecord['status'])
                        }
                      >
                        <option value="planned">Planned</option>
                        <option value="in_progress">In progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </CFormSelect>
                    </div>
                    <p>{item.scope}</p>
                    <div className="record-meta">
                      <span>Starts {formatDateTime(item.startsAt)}</span>
                      {item.nextDueAt && <span>Next due {formatDateTime(item.nextDueAt)}</span>}
                    </div>
                    {item.result && (
                      <div className="result-box">
                        <strong>Result</strong>
                        <p>{item.result}</p>
                      </div>
                    )}
                    {item.internalNotes && (
                      <details>
                        <summary>Internal notes</summary>
                        <p>{item.internalNotes}</p>
                      </details>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                title="No maintenance records"
                description="Plan the first baseline diagnostic and preventive inspection."
              />
            ))}
        </>
      )}
      <CModal visible={modal} onClose={() => setModal(false)} size="lg">
        <CModalHeader>
          <CModalTitle>{tab === 'incidents' ? 'Open incident' : 'Plan maintenance'}</CModalTitle>
        </CModalHeader>
        {tab === 'incidents' ? (
          <CForm onSubmit={submitIncident}>
            <CModalBody>
              {createIncident.error && (
                <CAlert color="danger">{apiErrorMessage(createIncident.error)}</CAlert>
              )}
              <div className="form-grid">
                <label className="form-grid__full">
                  Title
                  <CFormInput
                    value={incidentForm.title}
                    onChange={(event) => setIncidentForm({ ...incidentForm, title: event.target.value })}
                    required
                  />
                </label>
                <label>
                  Severity
                  <CFormSelect
                    value={incidentForm.severity}
                    onChange={(event) =>
                      setIncidentForm({ ...incidentForm, severity: event.target.value })
                    }
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </CFormSelect>
                </label>
                <label>
                  Detected at
                  <CFormInput
                    type="datetime-local"
                    value={incidentForm.detectedAt}
                    onChange={(event) =>
                      setIncidentForm({ ...incidentForm, detectedAt: event.target.value })
                    }
                    required
                  />
                </label>
                <label className="form-grid__full">
                  Public-safe summary
                  <CFormTextarea
                    rows={3}
                    value={incidentForm.publicSummary}
                    onChange={(event) =>
                      setIncidentForm({ ...incidentForm, publicSummary: event.target.value })
                    }
                  />
                </label>
                <label className="form-grid__full">
                  Internal investigation notes
                  <CFormTextarea
                    rows={5}
                    value={incidentForm.internalNotes}
                    onChange={(event) =>
                      setIncidentForm({ ...incidentForm, internalNotes: event.target.value })
                    }
                  />
                </label>
              </div>
            </CModalBody>
            <CModalFooter>
              <CButton color="light" onClick={() => setModal(false)}>
                Cancel
              </CButton>
              <CButton color="primary" type="submit">
                Create incident
              </CButton>
            </CModalFooter>
          </CForm>
        ) : (
          <CForm onSubmit={submitMaintenance}>
            <CModalBody>
              {createMaintenance.error && (
                <CAlert color="danger">{apiErrorMessage(createMaintenance.error)}</CAlert>
              )}
              <div className="form-grid">
                <label className="form-grid__full">
                  Title
                  <CFormInput
                    value={maintenanceForm.title}
                    onChange={(event) =>
                      setMaintenanceForm({ ...maintenanceForm, title: event.target.value })
                    }
                    required
                  />
                </label>
                <label className="form-grid__full">
                  Scope
                  <CFormTextarea
                    rows={3}
                    value={maintenanceForm.scope}
                    onChange={(event) =>
                      setMaintenanceForm({ ...maintenanceForm, scope: event.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  Starts at
                  <CFormInput
                    type="datetime-local"
                    value={maintenanceForm.startsAt}
                    onChange={(event) =>
                      setMaintenanceForm({ ...maintenanceForm, startsAt: event.target.value })
                    }
                    required
                  />
                </label>
                <label>
                  Next due (optional)
                  <CFormInput
                    type="datetime-local"
                    value={maintenanceForm.nextDueAt}
                    onChange={(event) =>
                      setMaintenanceForm({ ...maintenanceForm, nextDueAt: event.target.value })
                    }
                  />
                </label>
                <label className="form-grid__full">
                  Public-safe summary
                  <CFormTextarea
                    rows={3}
                    value={maintenanceForm.publicSummary}
                    onChange={(event) =>
                      setMaintenanceForm({ ...maintenanceForm, publicSummary: event.target.value })
                    }
                  />
                </label>
                <label className="form-grid__full">
                  Internal notes
                  <CFormTextarea
                    rows={4}
                    value={maintenanceForm.internalNotes}
                    onChange={(event) =>
                      setMaintenanceForm({ ...maintenanceForm, internalNotes: event.target.value })
                    }
                  />
                </label>
              </div>
            </CModalBody>
            <CModalFooter>
              <CButton color="light" onClick={() => setModal(false)}>
                Cancel
              </CButton>
              <CButton color="primary" type="submit">
                Save plan
              </CButton>
            </CModalFooter>
          </CForm>
        )}
      </CModal>
    </>
  )
}
